import {
  Autocomplete,
  AutocompleteGetTagProps,
  AutocompleteProps,
  AutocompleteRenderGroupParams,
  Box,
  styled,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';
import { compact, uniq } from 'lodash';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { StationFilterChip } from './display/StationFilterChip';
import {
  generateOptionId,
  getGroupHeader,
  getOptionColor,
  getOptionIcon,
  getStationTypeLabel,
  sortOptionsByType,
} from './StationFilterUtils';
import { OptionType, StationFilterOption } from './types';
import { MinimumStationsGroup, Station, StationType } from '@app/core/types/stations';

// Re-export the type for backward compatibility
export type { StationFilterOption };

// Component props
interface StationsAdvancedFilterProps extends Pick<
  AutocompleteProps<StationFilterOption, boolean, boolean, boolean>,
  'multiple' | 'disabled' | 'size'
> {
  stations: readonly Station[];
  stationGroups?: readonly MinimumStationsGroup[];
  value?: StationFilterOption[];
  onChange: (selectedOptions: StationFilterOption[]) => void;
  placeholder?: string;
  label?: string;
  error?: boolean;
  helperText?: string;
}

const GroupHeader = styled('div')<{ bgColor?: string }>(
  ({ theme, bgColor }: { theme: Theme; bgColor?: string }) => ({
    position: 'sticky',
    top: '-8px',
    padding: '12px 8px',
    color: theme.palette.text.primary,
    backgroundColor:
      bgColor ||
      (theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.grey[100]),
    fontWeight: '500',
  }),
);

const GroupItems = styled('ul')({
  padding: 0,
  marginBottom: '12px',
});

/**
 * Station filter autocomplete. Use `size="small"` for a 40px-tall control aligned with MUI small inputs.
 */
export const StationsAdvancedFilter = ({
  stations,
  stationGroups = [],
  value = [],
  onChange,
  multiple = true,
  disabled = false,
  size = 'medium',
  placeholder,
  label,
  error = false,
  helperText,
}: StationsAdvancedFilterProps) => {
  const { $t } = useIntl();
  const theme = useTheme();

  // Sort the value array for display while maintaining correct indices for deletion
  const sortedValue = useMemo(() => sortOptionsByType([...value]), [value]);

  // Handle onChange to maintain sorted order
  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: StationFilterOption[] | StationFilterOption | null,
  ) => {
    const arrayValue = Array.isArray(newValue) ? newValue : newValue ? [newValue] : [];
    onChange(arrayValue);
  };

  // Create all available options
  const allOptions = useMemo(() => {
    const options: StationFilterOption[] = [];

    // Add station type options
    const stationTypes: StationType[] = [
      StationType.STANDALONE,
      StationType.HEADER,
      StationType.REPEATER,
      StationType.AFFILIATE,
    ];

    // Restrict type filters to types that appear in `stations` when we have any; otherwise show all.
    const typesPresentInStations = uniq(compact(stations.map((s) => s.type)));
    const filteredStationTypes =
      typesPresentInStations.length > 0
        ? stationTypes.filter((type) => typesPresentInStations.includes(type))
        : stationTypes;

    filteredStationTypes.forEach((type) => {
      options.push({
        type: OptionType.StationType,
        value: type,
        label: getStationTypeLabel($t, type),
      });
    });

    // Add station group options
    stationGroups.forEach((group) => {
      options.push({
        label: group.name,
        type: OptionType.StationGroup,
        value: group,
      });
    });

    // Add individual station options
    stations.forEach((station) => {
      options.push({
        label: station.name,
        type: OptionType.IndividualStation,
        value: station,
      });
    });

    return options;
  }, [stations, stationGroups, $t]);

  // Group options by type for better organization
  const groupedOptions = useMemo(() => {
    return allOptions.sort((a, b) => {
      // First sort by type
      const typeOrder = [
        OptionType.StationType,
        OptionType.StationGroup,
        OptionType.IndividualStation,
      ];
      const aTypeIndex = typeOrder.indexOf(a.type);
      const bTypeIndex = typeOrder.indexOf(b.type);

      if (aTypeIndex !== bTypeIndex) {
        return aTypeIndex - bTypeIndex;
      }

      // Then sort alphabetically within each type
      return a.label.localeCompare(b.label);
    });
  }, [allOptions]);

  // Custom render option with icon
  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: StationFilterOption,
  ) => (
    <Box component="li" {...props}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
        <Box sx={{ color: '#bbb' }}>{getOptionIcon(option.type)}</Box>
        <Typography variant="body2">{option.label}</Typography>
      </Box>
    </Box>
  );

  const chipSize = size === 'small' ? 'small' : 'medium';

  // Custom render tags (chips) with icons
  const renderTags = (
    tagValue: readonly StationFilterOption[],
    getTagProps: AutocompleteGetTagProps,
  ) => {
    return tagValue.map((option, index) => {
      const { onDelete, key, ...tagProps } = getTagProps({ index });
      return (
        <Box key={key} {...tagProps} style={{ marginTop: 0, marginBottom: 0 }}>
          <StationFilterChip
            option={option}
            size={chipSize}
            onDelete={onDelete as unknown as () => void}
            readonly={false}
          />
        </Box>
      );
    });
  };

  // Custom group header renderer
  const renderGroup = (params: AutocompleteRenderGroupParams) => {
    const { group, children } = params;
    const type = groupedOptions.find((opt) => getGroupHeader($t, opt.type, 'long') === group)?.type;

    return (
      <li key={group}>
        <GroupHeader bgColor={type ? getOptionColor(type, theme) : undefined}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {type && getOptionIcon(type)}
            {group}
          </Box>
        </GroupHeader>
        <GroupItems>{children}</GroupItems>
      </li>
    );
  };

  // only show placeholder if no options are selected
  const showPlaceholder = !sortedValue.length;

  return (
    <Autocomplete
      multiple={multiple}
      size={size}
      disableCloseOnSelect
      filterSelectedOptions
      disabled={disabled}
      options={groupedOptions}
      value={sortedValue}
      onChange={handleChange}
      groupBy={(option) => getGroupHeader($t, option.type, 'long')}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => generateOptionId(option) === generateOptionId(value)}
      renderOption={renderOption}
      renderTags={renderTags}
      renderGroup={renderGroup}
      renderInput={(params) => (
        <TextField
          {...params}
          size={size}
          error={error}
          helperText={helperText}
          label={
            label ??
            $t({
              id: 'ui.stationsFilter.label',
              defaultMessage: 'Filtro de emisoras',
            })
          }
          placeholder={
            showPlaceholder
              ? (placeholder ??
                $t({
                  id: 'ui.stationsFilter.placeholder',
                  defaultMessage: 'Seleccionar una o varias opciones ',
                }))
              : undefined
          }
        />
      )}
      sx={{
        minWidth: 300,
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'background.paper',
        },
        ...(size === 'small' && {
          '& .MuiInputBase-root': {
            rowGap: '4px',
          },
          '& .MuiOutlinedInput-root': {
            minHeight: 40,
            backgroundColor: 'background.paper',
          },
        }),
      }}
    />
  );
};
